/**
 * Markdown utilities for tracker notes.
 *
 * The tracker stores notes as markdown (round-tripped through TipTap +
 * tiptap-markdown). For places that show only a short preview — like the
 * EntryCalendar cell — we strip the markdown markers but keep the text.
 *
 * `stripMarkdownToText` is intentionally a regex-only strip. We don't want to
 * pull in a parser for a tiny preview, and we'd rather under-strip a weird
 * edge case than risk losing the user's actual content. Newlines are
 * preserved verbatim; only runs of 3+ blank lines collapse to one. The DB
 * value is never mutated — this is for display only.
 */

/**
 * Strip common markdown markers from `markdown` and return plain text
 * suitable for a small inline preview. Preserves all `\n` characters.
 *
 * Handles: headings (`#`..`######`), bold (`**`/`__`), italic (`*`/`_`),
 * inline code (`` ` ``), code fences (``` ``` ```), blockquotes (`> `),
 * horizontal rules (`---`/`***`/`___`), unordered/ordered list markers
 * (`-`, `*`, `+`, `1.`), and inline links (`[text](url)` → `text`).
 *
 * Does NOT attempt to render HTML, math, tables, or images. Anything
 * unexpected falls through unchanged.
 */
export function stripMarkdownToText(markdown: string): string {
  if (!markdown) return "";

  let out = markdown;

  // Code fences first so their content isn't accidentally re-processed
  // by other rules. We keep the inner text but drop the fence delimiters.
  out = out.replace(/```[a-zA-Z0-9_-]*\n?([\s\S]*?)```/g, "$1");

  // Inline code: `code` -> code
  out = out.replace(/`([^`\n]+)`/g, "$1");

  // Inline links: [label](url) -> label
  out = out.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  // Bare autolink: <https://...> -> https://...
  out = out.replace(/<((?:https?|mailto):[^>]+)>/g, "$1");

  // Heading markers at start of line: ###### Heading -> Heading
  out = out.replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "");

  // Blockquote markers at start of line: > text -> text
  out = out.replace(/^[ \t]{0,3}>[ \t]?/gm, "");

  // Horizontal rule lines (---, ***, ___ on a line by themselves).
  // Replace the line content with empty; the surrounding \n remains.
  out = out.replace(/^[ \t]{0,3}(?:[-*_])(?:[ \t]*[-*_]){2,}[ \t]*$/gm, "");

  // Unordered list markers: -, *, + at start of line
  out = out.replace(/^[ \t]{0,3}[-*+][ \t]+/gm, "");

  // Ordered list markers: 1. 2. 999. at start of line
  out = out.replace(/^[ \t]{0,3}\d+[.)][ \t]+/gm, "");

  // Bold + italic: **strong**, __strong__, *em*, _em_
  // Run bold first (longer marker) so the italic rule doesn't eat its delimiters.
  out = out.replace(/\*\*([^*\n]+)\*\*/g, "$1");
  out = out.replace(/__([^_\n]+)__/g, "$1");
  out = out.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "$1");
  out = out.replace(/(?<!_)_([^_\n]+)_(?!_)/g, "$1");

  // Collapse runs of 3+ blank lines to one blank line. Keep all other
  // newlines exactly as-is so paragraph structure survives the strip.
  out = out.replace(/\n{3,}/g, "\n\n");

  return out;
}
